/**
 * Dictado por voz para el composer del Agente CEO.
 *
 * Usa la Web Speech API del navegador: cero costo, cero latencia de red y la voz
 * no sale del dispositivo. Lo transcrito **cae en el cuadro de texto** para que
 * la persona lo revise; nunca se envía solo. Un reconocedor se equivoca con
 * nombres y cifras, y mandar una pregunta mal transcrita al agente produce una
 * respuesta correcta a la pregunta equivocada, que es peor que no dictar.
 *
 * PLAN B, si la calidad del navegador no alcanza (documentado, sin implementar):
 * una Edge Function `ia-transcribir` que reciba el audio grabado con MediaRecorder
 * y lo mande a Whisper con `OPENAI_API_KEY` —ese secreto ya existe y NO sirve
 * para Anthropic, que lo rechaza con 401—. Costaría ~US$0.006 por minuto y
 * agregaría el viaje de red, pero transcribe mucho mejor el español con nombres
 * propios. El contrato sería el mismo: devuelve texto al cuadro, no envía.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** Tope duro: dos minutos. Más que eso ya no es dictar una pregunta. */
const LIMITE_MS = 2 * 60 * 1000;

type Reconocedor = any;

const SpeechRecognitionAPI = (): Reconocedor | null => {
  if (typeof window === "undefined") return null;
  return (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition ?? null;
};

export function useDictado(onTexto: (texto: string) => void) {
  const [grabando, setGrabando] = useState(false);
  const [parcial, setParcial] = useState("");
  const recRef = useRef<Reconocedor>(null);
  const tempRef = useRef<number | null>(null);

  const soportado = SpeechRecognitionAPI() !== null;

  const detener = useCallback(() => {
    try {
      recRef.current?.stop();
    } catch {
      /* ya estaba detenido */
    }
    recRef.current = null;
    if (tempRef.current) {
      clearTimeout(tempRef.current);
      tempRef.current = null;
    }
    setGrabando(false);
    setParcial("");
  }, []);

  // Si el componente se desmonta a media grabación, el micrófono debe soltarse.
  useEffect(() => detener, [detener]);

  const iniciar = useCallback(() => {
    const API = SpeechRecognitionAPI();
    if (!API) return;

    const rec = new API();
    rec.lang = "es-MX";
    rec.continuous = true;
    rec.interimResults = true; // se ve lo que va entendiendo, no solo al final

    rec.onresult = (e: any) => {
      let confirmado = "";
      let enVuelo = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) confirmado += t;
        else enVuelo += t;
      }
      if (confirmado) onTexto(confirmado);
      setParcial(enVuelo);
    };

    rec.onerror = () => detener();
    rec.onend = () => {
      recRef.current = null;
      setGrabando(false);
      setParcial("");
    };

    recRef.current = rec;
    rec.start();
    setGrabando(true);
    tempRef.current = window.setTimeout(detener, LIMITE_MS);
  }, [detener, onTexto]);

  return { soportado, grabando, parcial, iniciar, detener, limiteSegundos: LIMITE_MS / 1000 };
}
